import {useLocation, useNavigate} from 'react-router-dom';
import {useEffect, useState} from 'react';
import {AccountData, getBankList, validateId} from "../../api/commonApi";
import {validatePhone} from "../../api/passApi";
import AreaSelectComponent from '../../components/common/AreaSelectComponent';
import {alertAfterFunction, defaultAlert} from "../../utils/alert";
import {AuthBackDepositorTypes} from "../../types/common/bank.account.types";
import {checkDepositor} from "../../api/authApi";
import useFirebaseToken from "../../hooks/useFireBaseToken";
import Select from "react-select";
import {useMemberSetting} from "../../hooks/useMemberSetting";
import {validatePassword} from "../../utils/validation";
import useIosInputAutoScroll from "../../hooks/useIosInputAutoScroll";

const SignupWriteDataPage = () => {
    const location = useLocation();
    const {agreedTerms, passVerifyData} = location.state || {};
    const niceData = passVerifyData?.data || {};

    // NICE 데이터 파싱
    const memberName = niceData.name || '';
    const phone = niceData.mobile_no || '';
    const birthday = niceData.birthdate || ''; // "19920913"
    const genderCode = niceData.gender || ''; // "1"=남성, "2"=여성
    const gender = genderCode === '1' ? 'M' : 'F';
    const genderLabel = genderCode === '1' ? '남성' : '여성';

    // 생년월일 포맷 변환: "19920913" → "1992년 9월 13일"
    const formatBirthday = (bd: string) => {
        if (!bd || bd.length !== 8) return bd;
        const year = bd.substring(0, 4);
        const month = parseInt(bd.substring(4, 6));
        const day = parseInt(bd.substring(6, 8));
        return `${year}년 ${month}월 ${day}일`;
    };

    // 나이 계산 (만 나이)
    const calcAge = (bd: string) => {
        if (!bd || bd.length < 4) return 0;
        const birthYear = parseInt(bd.substring(0, 4));
        const birthMonth = parseInt(bd.substring(4, 6)) - 1;
        const birthDay = parseInt(bd.substring(6, 8));
        const today = new Date();
        let age = today.getFullYear() - birthYear;
        if (today < new Date(today.getFullYear(), birthMonth, birthDay)) age--;
        return age;
    };
    const {memberSetting, memberSettingLoading} = useMemberSetting();
    const navigate = useNavigate();
    const [bankList, setBankList] = useState<AccountData[]>([]);
    // 지역 선택 관련 state
    const [selectedMainArea, setSelectedMainArea] = useState<string>("");
    const [selectedSubArea, setSelectedSubArea] = useState<string>("");
    const [isEmailChecked, setIsEmailChecked] = useState<boolean>(false);
    const [iAccountChecked, setIsAccountChecked] = useState<boolean>(false);
    const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
    const [isPasswordMatching, setIsPasswordMatching] = useState<boolean>(false);
    const {fcmToken, deviceType, isTokenReady} = useFirebaseToken();
    useIosInputAutoScroll();

    const bankOptions = bankList.map(bank => ({
        value: bank.id.coCode,
        label: bank.coName,
    }));

    const [form, setForm] = useState({
        email: '',
        password: '',
        passwordConfirm: '',
        bankCode: '',
        accountNo: ''
    });

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
        const {name, value} = e.target;
        if (name === 'email') {
            setIsEmailChecked(false);
        }
        if (name === 'bankCode' || name === 'accountNo') {
            setIsAccountChecked(false);
        }

        if (name === 'accountNo') {
            setForm(prev => ({ ...prev, [name]: value.replace(/\D/g, '') }));
            return;
        }

        setForm(prev => ({
            ...prev,
            [name]: value
        }));

        // 비밀번호 유효성 검사
        if (name === 'password') {
            setIsPasswordValid(validatePassword(value));
            setIsPasswordMatching(value === form.passwordConfirm && validatePassword(value));
        }

        // 비밀번호 확인
        if (name === 'passwordConfirm') {
            setIsPasswordMatching(form.password === value && validatePassword(form.password));
        }
    };

    // 은행 목록 조회 + 핸드폰 번호 중복 체크
    useEffect(() => {
        if (!agreedTerms) {
            navigate('/signup/terms');
            return;
        }

        const init = async () => {
            try {
                const res = await getBankList();
                setBankList(res.data.data);
            } catch (error) {
                console.error('Failed to fetch BankList:', error);
            }

            if (phone) {
                try {
                    const isDuplicate = await validatePhone(phone);
                    if (isDuplicate) {
                        defaultAlert('이미 가입되어 있는 핸드폰 번호입니다.');
                        navigate('/login', { replace: true });
                    }
                } catch (error) {
                    console.error('Failed to validate phone:', error);
                }
            }
        };

        init();
    }, []);

    useEffect(() => {
        if (memberSettingLoading) return;  // 로딩 중이면 아무것도 안 함
        ageCheck();
    }, [memberSetting, memberSettingLoading]);

    const handleEmailCheck = async () => {
        if (!form.email) {
            defaultAlert('이메일을 입력해주세요.');
            return;
        }

        // 이메일 형식 검증
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(form.email)) {
            defaultAlert('올바른 이메일 형식이 아닙니다.');
            return;
        }

        try {
            const res = await validateId(form.email);
            if (res.data.data) {
                defaultAlert('이미 사용중인 이메일입니다.');
                setIsEmailChecked(false);
            } else {
                defaultAlert('사용 가능한 이메일입니다.');
                setIsEmailChecked(true);
            }
        } catch (error) {
            console.error('Failed to check email:', error);
            defaultAlert('이메일 중복 확인에 실패했습니다.');
            setIsEmailChecked(false);
        }
    };
    const handleSignup = async () => {

        if (!isTokenReady) {
            console.warn("FCM 토큰 준비 중...");
            return;
        }

        if (!isEmailChecked) {
            defaultAlert('이메일 중복확인을 해주세요.');
            return;
        }

        if (!isPasswordValid) {
            defaultAlert('비밀번호는 영문 대소문자/숫자/특수문자 중 2가지 이상 조합, 8자~20자여야 합니다.');
            return;
        }

        if (form.password !== form.passwordConfirm) {
            defaultAlert('비밀번호가 일치하지 않습니다.');
            return;
        }

        if (!iAccountChecked) {
            defaultAlert('계좌인증을 해주세요.');
            return;
        }

        // // sessionStorage에 회원가입 정보 임시 저장
        sessionStorage.setItem('signupData', JSON.stringify({
            memberName,
            phone,
            birthday,
            gender,
            memberId: form.email,
            password: form.password,
            bankCode: form.bankCode,
            accountNo: form.accountNo,
            preferredAreaMajor: selectedMainArea,
            preferredAreaMinor: selectedSubArea,
            termsAgreeList: agreedTerms,
            fcmToken: fcmToken ?? null,
            deviceType: deviceType ?? null,
        }));

        // 결제 비밀번호 설정 페이지로 이동
        navigate('/set-payment-password?mode=signup');
    };

    const checkBankAccountDepositor = async () => {
        try {
            if (!form.bankCode) {
                defaultAlert("은행를 선택해주세요");
                return;
            }
            if (!form.accountNo) {
                defaultAlert("계좌번호를 입력해주세요");
                return;
            }
            const bankType: AuthBackDepositorTypes = {
                memberName,
                bankCode: form.bankCode,
                accountNo: form.accountNo
            };

            const res = await checkDepositor(bankType);

            if (res.data.code === "00") {
                defaultAlert("계좌 인증에 성공하였습니다.");
                setIsAccountChecked(true);
            } else {
                defaultAlert(res.data.message || "계좌 인증에 실패했습니다.");
                setIsAccountChecked(false);
                return;
            }
        } catch (err) {
            console.error(err);
            defaultAlert("계좌 인증에 실패했습니다.");
            setIsAccountChecked(false);
        }
    };

    const ageCheck = () => {
        if (!memberSetting) return;

        const minAgeEnabled = memberSetting?.minAgeEnabled || true;
        const returnAge = calcAge(birthday);
        if (minAgeEnabled && returnAge) {
            const minAge = memberSetting?.minAge || 14;
            if (minAge > returnAge) {
                alertAfterFunction("오또케어는 " + minAge + "세 이상만 가입 가능합니다.", () =>
                    navigate("/")
                );
            }
        }
    };
    return (
        <>
            <header className="sub-header">
                <div className="header-inner">
                    <button type="button" className="backBtn" onClick={() => navigate('/signup/terms', { replace: true })}>
                        <span className="blind">뒤로가기</span>
                    </button>
                </div>
            </header>

            <div className="bottom-btn">
                <button type="button" className="btn cancel" onClick={() => navigate('/signup/terms', { replace: true })}>이전</button>
                <button
                    type="button"
                    className="btn confirm"
                    onClick={handleSignup}
                    disabled={!isEmailChecked || !isPasswordMatching || !iAccountChecked}
                >
                    회원가입
                </button>
            </div>

            <main className="container">
                <section className="tit-box-wrap">
                    <h2 className="headline">신청정보</h2>
                    <p className="subtit">회원 가입을 위하여 정보를 입력해주세요.</p>
                    <ol className="join-process">
                        <li><span>1</span></li>
                        <li className="on"><span>2</span></li>
                    </ol>
                </section>

                <section>
                    <div className="inner">
                        <div className="round-box">
                            <div className="round-body">
                                <div className="info-row">
                                    <span className="label">이름</span>
                                    <span className="cont">{memberName}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">휴대폰 번호</span>
                                    <span className="cont">{phone}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">생년월일</span>
                                    <span className="cont">{formatBirthday(birthday)}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">성별</span>
                                    <span className="cont">{genderLabel}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section>
                    <div className="inner-box">
                        <form className="form-box">
                            <fieldset>
                                <legend>회원가입 입력 폼</legend>

                                <div className="form-item">
                                    <label className="input-tit">회원 ID</label>
                                    <div className="input-set">
                                        <div className="input-box">
                                            <input
                                                type="text"
                                                name="email"
                                                placeholder="E-mail 주소를 입력해주세요"
                                                value={form.email}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <button type="button" className="check-btn" onClick={handleEmailCheck}>중복확인
                                        </button>
                                    </div>
                                </div>

                                <div className="form-item">
                                    <label className="input-tit">비밀번호</label>
                                    <div className="input-box">
                                        <input
                                            type="password"
                                            name="password"
                                            placeholder="비밀번호 설정(영문 대소문자/숫자/특수문자 중 2가지 이상 8자~20자)"
                                            value={form.password}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    {form.password && !isPasswordValid && (
                                        <p className="error-msg">비밀번호는 영문 대소문자/숫자/특수문자 중 2가지 이상 조합, 8자~20자여야 합니다.</p>
                                    )}
                                    {form.password && isPasswordValid && (
                                        <p className="success-msg">유효한 비밀번호입니다.</p>
                                    )}
                                </div>

                                <div className="form-item">
                                    <label className="input-tit">비밀번호 확인</label>
                                    <div className="input-box">
                                        <input
                                            type="password"
                                            name="passwordConfirm"
                                            placeholder="비밀번호를 다시 한번 입력해주세요"
                                            value={form.passwordConfirm}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    {form.passwordConfirm && !isPasswordMatching && (
                                        <p className="error-msg">비밀번호가 일치하지 않습니다.</p>
                                    )}
                                    {form.passwordConfirm && isPasswordMatching && (
                                        <p className="success-msg">비밀번호가 일치합니다.</p>
                                    )}
                                </div>

                                <div className="form-item">
                                    <label className="input-tit">계좌 등록</label>
                                    <div className="custom-select-react">
                                        <Select
                                            placeholder="은행을 선택해 주세요"
                                            value={bankOptions.find(o => o.value === form.bankCode) ?? null}
                                            onChange={(opt) =>
                                                handleChange({
                                                    target: { name: "bankCode", value: opt?.value ?? "" }
                                                } as React.ChangeEvent<HTMLSelectElement>)
                                            }
                                            options={bankOptions}
                                            isSearchable={false}
                                            isDisabled={iAccountChecked}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            }}
                                        />
                                    </div>
                                    <div className="input-set mt10">
                                        <div className="input-box">
                                            <input
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                type="text"
                                                name="accountNo"
                                                placeholder="계좌번호를 입력해주세요"
                                                value={form.accountNo}
                                                onChange={handleChange}
                                                disabled={iAccountChecked}
                                            />
                                        </div>
                                        {!iAccountChecked && (
                                            <button type="button" className="check-btn" onClick={checkBankAccountDepositor}>계좌인증</button>
                                        )}
                                        {iAccountChecked && (
                                            <span className="check-btn" style={{background:'#ccc', cursor:'default', display:'inline-flex', alignItems:'center', justifyContent:'center'}}>인증완료</span>
                                        )}
                                    </div>
                                </div>
                                <div className="form-item">
                                    <label className="input-tit">선호 지역</label>
                                    <AreaSelectComponent
                                        selectedMainArea={selectedMainArea}
                                        selectedSubArea={selectedSubArea}
                                        onMainAreaChange={setSelectedMainArea}
                                        onSubAreaChange={setSelectedSubArea}
                                        required={true}
                                    />
                                </div>

                            </fieldset>
                        </form>
                    </div>
                </section>
            </main>
        </>
    );
};

export default SignupWriteDataPage;